import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { isValidDatePattern, isValidTimePattern } from '~/types/leLens';
import type { LensCard, LensCardType, LensXAxisConfig } from '~/types/leLens';

import AxisRangeControl from '../../AxisRangeControl';
import { MAX_SERIES, displayLensFieldName, formatLensLabel, formatLensValue } from '../../lensChart';
import CardSettingDialog from '../CardSettingDialog';

import {
  PATTERN_SAMPLE,
  type StyleSection,
  type StyleTabState,
  TEXT_SCALES,
  TIME_PATTERN_SAMPLE,
  formatKinds,
  numberFieldNumber,
  seriesSorts,
} from './useStyleTabState';

// Lifted from LensCardDialog.tsx's Format & style tab: the controls are
// grouped by the job they change (labels, numbers, axes, series and guides)
// controls, same copy. State and derivation live in useStyleTabState; this
// component only renders it, reading `state` (from the hook) plus a handful
// of read-only context props the hook does not itself need.

// EM_DASH avoids a literal em dash character in the source; the two spots
// below render identically to the legacy dialog's copy.
const EM_DASH = '\u2014';

/** Visual settings stay readable in the pane; their full forms open in a dialog. */
function FormatSubRow({
  children,
  icon,
  label,
  summary,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  label: string;
  summary: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <CardSettingDialog
      contentId={`lens-card-setting-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      icon={icon}
      label={label}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      summary={summary}
    >
      {children}
    </CardSettingDialog>
  );
}

export type StyleTabProps = {
  /** For displayLensFieldName(name, card.config), the same lookup the
   *  legacy dialog used to show a series' renamed label beside its raw name. */
  card: LensCard;
  type: LensCardType;
  /** Every field the card shows, for the per-field formatting section. */
  fieldNames: Array<string>;
  /** Series from the last render, for colours, marks and value labels. */
  seriesNames: Array<string>;
  /** Declared columns, for the heatmap/box-plot grid-field selects. */
  columnNames: Array<string>;
  valueExtent?: { min: number; max: number };
  state: StyleTabState;
  /** Keep chart mechanics with Chart, and number/text finishing with Appearance. */
  scope: 'chart' | 'appearance';
  busy?: boolean;
  isSaving?: boolean;
};

/**
 * The panel's detailed visual controls, split by job across Chart and
 * Appearance. Every control reads and writes through `state`
 * (useStyleTabState), which reports the computed config live for the
 * preview seam.
 */
export default function StyleTab({
  busy = false,
  card,
  columnNames,
  fieldNames,
  isSaving = false,
  seriesNames,
  scope,
  state,
  type,
  valueExtent,
}: StyleTabProps) {
  if (state.styleSections.length === 0) {
    return null;
  }

  const sectionSummary = (id: StyleSection): string =>
    state.styleSections.find((entry) => entry.id === id)?.summary ?? '';

  return (
    <Box sx={{ pt: 0.5 }}>
      {scope === 'appearance' && state.showsValueFormat && (
        <FormatSubRow
          icon={<NumbersOutlinedIcon fontSize="small" />}
          label="Number details"
          summary={`Suffix, chart text and field formats · ${sectionSummary('numbers')}`}
        >
          <Stack spacing={2.5}>
            {state.showsValueAxis && (
              <FormControl sx={{ maxWidth: 320 }}>
                <InputLabel id="le-lens-text-scale">Chart text</InputLabel>
                <Select
                  disabled={busy}
                  label="Chart text"
                  labelId="le-lens-text-scale"
                  onChange={(event) => state.setTextScale(event.target.value)}
                  value={state.textScale}
                >
                  {TEXT_SCALES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Changes axis labels, axis titles, legend and printed values together.</FormHelperText>
              </FormControl>
            )}

            <Stack spacing={2.5}>
              <TextField
                disabled={isSaving}
                fullWidth
                helperText="Optional text after the value, such as “per booking”."
                label="Suffix"
                onChange={(event) => state.setSuffix(event.target.value)}
                value={state.suffix}
              />

              <Divider />

              {fieldNames.length > 1 && (
                <Box>
                  <Typography sx={{ mb: 1 }} variant="subtitle2">
                    Per-field formatting
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                    Give a query field a business name without changing the query. A field set to “Card default” uses
                    the format above; override the ones that differ, such as a revenue column beside a conversion rate.
                  </Typography>
                  <Stack spacing={1.5}>
                    {fieldNames.map((field) => {
                      const override = state.fieldFormats[field];
                      const renamedField = state.fieldLabels[field]?.trim();
                      const isEditingField = state.editingField === field;

                      return (
                        <Stack alignItems="center" direction="column" key={field} spacing={1.5}>
                          <Box
                            sx={{
                              flexBasis: '20%',
                              flexShrink: 0,
                              minWidth: 0,
                              width: '100%',
                            }}
                          >
                            {isEditingField ? (
                              <Stack alignItems="center" direction="row" spacing={0.25}>
                                <TextField
                                  autoFocus
                                  disabled={isSaving}
                                  fullWidth
                                  inputProps={{ 'aria-label': `Display name for ${field}` }}
                                  label="Display name"
                                  onChange={(event) => state.setFieldLabelDraft(event.target.value)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      state.saveFieldLabel(field);
                                    }
                                    if (event.key === 'Escape') {
                                      state.setEditingField(null);
                                    }
                                  }}
                                  size="small"
                                  value={state.fieldLabelDraft}
                                />
                                <IconButton
                                  aria-label={`Save display name for ${field}`}
                                  color="primary"
                                  disabled={isSaving}
                                  onClick={() => state.saveFieldLabel(field)}
                                  size="small"
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label={`Cancel renaming ${field}`}
                                  disabled={isSaving}
                                  onClick={() => state.setEditingField(null)}
                                  size="small"
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ) : (
                              <>
                                <Stack alignItems="center" direction="row" spacing={0.25}>
                                  <Typography noWrap title={field} variant="body2">
                                    {field}
                                  </Typography>
                                  <IconButton
                                    aria-label={`Rename ${field}`}
                                    disabled={isSaving}
                                    onClick={() => state.startEditingField(field)}
                                    size="small"
                                  >
                                    <EditOutlinedIcon fontSize="inherit" />
                                  </IconButton>
                                </Stack>
                                <Typography color="text.secondary" noWrap title={renamedField} variant="caption">
                                  {renamedField ? `Shown as ${renamedField}` : 'Query field'}
                                </Typography>
                              </>
                            )}
                          </Box>
                          <FormControl fullWidth size="small">
                            <InputLabel id={`le-lens-field-kind-${field}`}>Format</InputLabel>
                            <Select
                              disabled={isSaving}
                              label="Format"
                              labelId={`le-lens-field-kind-${field}`}
                              onChange={(event) => {
                                const value = event.target.value;
                                if (value === 'default') {
                                  state.setFieldOverride(field, null);
                                  return;
                                }
                                state.setFieldOverride(field, {
                                  kind: value,
                                  ...(value === 'currency' ? { currency: override?.currency ?? state.currency } : {}),
                                  ...(override?.decimals === undefined ? {} : { decimals: override.decimals }),
                                });
                              }}
                              value={override?.kind ?? 'default'}
                            >
                              <MenuItem value="default">Card default</MenuItem>
                              {formatKinds.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl disabled={!override || isSaving} fullWidth size="small">
                            <InputLabel id={`le-lens-field-decimals-${field}`}>Decimals</InputLabel>
                            <Select
                              label="Decimals"
                              labelId={`le-lens-field-decimals-${field}`}
                              onChange={(event) => {
                                if (!override) {
                                  return;
                                }
                                const value = event.target.value;
                                state.setFieldOverride(field, {
                                  ...override,
                                  ...(value === 'auto' ? { decimals: undefined } : { decimals: Number(value) }),
                                });
                              }}
                              value={override?.decimals === undefined ? 'auto' : String(override.decimals)}
                            >
                              <MenuItem value="auto">Auto</MenuItem>
                              {[0, 1, 2, 3, 4].map((value) => (
                                <MenuItem key={value} value={String(value)}>
                                  {value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Stack>
        </FormatSubRow>
      )}

      {scope === 'chart' && state.showsAnyAxis && (
        <FormatSubRow icon={<TuneOutlinedIcon fontSize="small" />} label="Axes" summary={sectionSummary('axes')}>
          <Stack spacing={2.5}>
            <Box>
              {state.showsLabelFormat && (
                <>
                  <Typography sx={{ mb: 1.5 }} variant="subtitle2">
                    Axis labels
                  </Typography>
                  <Stack direction="column" spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel id="le-lens-label-kind">Label type</InputLabel>
                      <Select
                        disabled={isSaving}
                        label="Label type"
                        labelId="le-lens-label-kind"
                        onChange={(event) => {
                          const nextKind = event.target.value as never;
                          state.setLabelKind(nextKind);
                          if (nextKind === 'time' && !isValidTimePattern(state.labelPattern.trim())) {
                            state.setLabelPattern('HH:mm');
                            state.setIsCustomPattern(false);
                          }
                          if (nextKind === 'date' && !isValidDatePattern(state.labelPattern.trim())) {
                            state.setLabelPattern('dd/MM/yyyy');
                            state.setIsCustomPattern(false);
                          }
                        }}
                        value={state.labelKind}
                      >
                        <MenuItem value="text">As returned</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="time">Time</MenuItem>
                      </Select>
                    </FormControl>

                    {state.isFormattedLabel && (
                      <FormControl fullWidth>
                        <InputLabel id="le-lens-label-pattern">
                          {state.labelKind === 'time' ? 'Time' : 'Date'} format
                        </InputLabel>
                        <Select
                          disabled={isSaving}
                          label={`${state.labelKind === 'time' ? 'Time' : 'Date'} format`}
                          labelId="le-lens-label-pattern"
                          onChange={(event) => {
                            const next = event.target.value;
                            if (next === '__custom') {
                              state.setIsCustomPattern(true);
                              return;
                            }
                            state.setIsCustomPattern(false);
                            state.setLabelPattern(next);
                          }}
                          renderValue={(selected) => (state.isCustomPattern ? 'Custom…' : selected)}
                          value={state.isCustomPattern ? '__custom' : state.labelPattern}
                        >
                          {state.labelPatterns.map((pattern) => (
                            <MenuItem key={pattern} value={pattern}>
                              {`${pattern} ${EM_DASH} ${formatLensLabel(
                                state.labelKind === 'time' ? TIME_PATTERN_SAMPLE : PATTERN_SAMPLE,
                                { kind: state.labelKind === 'time' ? 'time' : 'date', pattern },
                              )}`}
                            </MenuItem>
                          ))}
                          <MenuItem value="__custom">Custom…</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                </>
              )}

              {state.showsValueAxis && (
                <>
                  <Typography sx={{ mb: 1.5, mt: 3 }} variant="subtitle2">
                    Value axis
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      disabled={busy}
                      fullWidth
                      label="Y-axis title"
                      onChange={(event) => state.setYAxisTitle(event.target.value)}
                      placeholder="None"
                      value={state.yAxisTitle}
                    />
                    <AxisRangeControl
                      allowFloor={state.showsAxisFloor}
                      disabled={busy}
                      label="Primary axis range"
                      maxLabel="End axis at"
                      maxValue={state.yAxisMax}
                      minLabel="Start axis at"
                      minValue={state.yAxisMin}
                      onMaxChange={state.setYAxisMax}
                      onMinChange={state.setYAxisMin}
                      valueExtent={valueExtent}
                      valueFormat={state.buildFormat()}
                    />
                  </Stack>
                  {state.truncatesFromZero && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {type === 'bar' || type === 'area'
                        ? `Marks are drawn from ${formatLensValue(numberFieldNumber(state.yAxisMin), state.buildFormat())}, so their heights compare distances above that floor rather than totals. Consider a line instead, which reads as a position and not a length.`
                        : 'The axis will not start at zero, which makes changes look larger than they are.'}{' '}
                      The card carries a note saying where the axis starts, on screen and in any exported image.
                    </Alert>
                  )}
                  {type === 'combo' && (
                    <>
                      <Typography sx={{ mb: 1.5, mt: 3 }} variant="subtitle2">
                        Second scale
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel id="le-lens-secondary-series">Series on the second scale</InputLabel>
                        <Select
                          disabled={busy}
                          input={<OutlinedInput label="Series on the second scale" />}
                          labelId="le-lens-secondary-series"
                          multiple
                          onChange={(event) =>
                            state.setSecondarySeries(
                              typeof event.target.value === 'string' ? [event.target.value] : event.target.value,
                            )
                          }
                          renderValue={(chosen) =>
                            chosen.length === 0
                              ? 'Chosen from the data'
                              : chosen.map((name) => displayLensFieldName(name, card.config)).join(', ')
                          }
                          value={state.secondarySeries}
                        >
                          {seriesNames.map((name) => (
                            <MenuItem key={name} value={name}>
                              {displayLensFieldName(name, card.config)}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Left empty, the card puts any series an order of magnitude below the largest on its own scale.
                          Choose here when that guess picks the wrong one.
                        </FormHelperText>
                      </FormControl>
                      {seriesNames.length > 0 && (
                        <Box>
                          <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
                            Draw each series as
                          </Typography>
                          <Stack direction="column" spacing={2}>
                            {seriesNames.slice(0, MAX_SERIES).map((name) => (
                              <FormControl fullWidth key={name}>
                                <InputLabel id={`le-lens-mark-${name}`}>
                                  {displayLensFieldName(name, card.config)}
                                </InputLabel>
                                <Select
                                  disabled={busy}
                                  label={displayLensFieldName(name, card.config)}
                                  labelId={`le-lens-mark-${name}`}
                                  onChange={(event) =>
                                    state.setSeriesMarks((current) => {
                                      const next = { ...current };
                                      if (event.target.value) {
                                        next[name] = event.target.value;
                                      } else {
                                        delete next[name];
                                      }
                                      return next;
                                    })
                                  }
                                  value={state.seriesMarks[name] ?? ''}
                                >
                                  <MenuItem value="">
                                    {state.secondarySeries.includes(name) ? 'Line (second scale)' : 'Bar'}
                                  </MenuItem>
                                  <MenuItem value="bar">Bar</MenuItem>
                                  <MenuItem value="line">Line</MenuItem>
                                  <MenuItem value="area">Area</MenuItem>
                                </Select>
                              </FormControl>
                            ))}
                          </Stack>
                          <Typography color="text.secondary" sx={{ display: 'block', mt: 1 }} variant="caption">
                            A line over bars is the readable convention for a second scale, which is what the card picks
                            on its own.
                          </Typography>
                        </Box>
                      )}
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                          disabled={busy}
                          fullWidth
                          label="Second axis title"
                          onChange={(event) => state.setY2AxisTitle(event.target.value)}
                          placeholder="None"
                          value={state.y2AxisTitle}
                        />
                        <AxisRangeControl
                          allowFloor
                          disabled={busy}
                          label="Second axis range"
                          maxLabel="Second axis ends at"
                          maxValue={state.y2AxisMax}
                          minLabel="Second axis starts at"
                          minValue={state.y2AxisMin}
                          onMaxChange={state.setY2AxisMax}
                          onMinChange={state.setY2AxisMin}
                          prefix="Second"
                          valueExtent={valueExtent}
                          valueFormat={state.buildFormat()}
                        />
                      </Stack>
                    </>
                  )}
                </>
              )}

              {state.showsXAxis && (
                <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                  <TextField
                    disabled={busy}
                    fullWidth
                    label="X-axis title"
                    onChange={(event) => state.setXAxisTitle(event.target.value)}
                    placeholder="None"
                    value={state.xAxisTitle}
                  />
                  {columnNames.length > 0 && (
                    <FormControl fullWidth>
                      <InputLabel id="le-lens-x-axis-column">X-axis field</InputLabel>
                      <Select
                        disabled={isSaving}
                        label="X-axis field"
                        labelId="le-lens-x-axis-column"
                        onChange={(event) => state.setXColumn(event.target.value)}
                        value={state.xColumn}
                      >
                        <MenuItem value="">Automatically choose the first label field</MenuItem>
                        {columnNames.map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl fullWidth>
                    <InputLabel id="le-lens-x-axis-density">Label density</InputLabel>
                    <Select
                      disabled={isSaving}
                      label="Label density"
                      labelId="le-lens-x-axis-density"
                      onChange={(event) => state.setXAxisDensity(event.target.value)}
                      value={state.xAxisDensity}
                    >
                      <MenuItem value="auto">Automatic {EM_DASH} keep labels readable</MenuItem>
                      <MenuItem value="all">Show every category</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="le-lens-x-axis-rotation">Label angle</InputLabel>
                    <Select
                      disabled={isSaving}
                      label="Label angle"
                      labelId="le-lens-x-axis-rotation"
                      onChange={(event) =>
                        state.setXAxisRotation(
                          Number(event.target.value) as NonNullable<LensXAxisConfig['tickRotation']>,
                        )
                      }
                      value={state.xAxisRotation}
                    >
                      <MenuItem value={0}>Horizontal</MenuItem>
                      <MenuItem value={30}>30°</MenuItem>
                      <MenuItem value={45}>45°</MenuItem>
                      <MenuItem value={60}>60°</MenuItem>
                      <MenuItem value={90}>Vertical</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              )}
              {state.isFormattedLabel && state.isCustomPattern && (
                <TextField
                  disabled={isSaving}
                  error={Boolean(state.labelPattern.trim()) && !state.labelPatternIsValid}
                  fullWidth
                  helperText={
                    state.labelPatternIsValid
                      ? `Preview: ${formatLensLabel(state.labelPatternSample, {
                          kind: state.labelKind === 'time' ? 'time' : 'date',
                          pattern: state.labelPattern.trim(),
                        })}`
                      : `Use ${state.labelPatternTokens.join(', ')} and separators such as / - . ,`
                  }
                  label={`Custom ${state.labelKind} format`}
                  onChange={(event) => state.setLabelPattern(event.target.value)}
                  sx={{ mt: 2 }}
                  value={state.labelPattern}
                />
              )}

              {state.isFormattedLabel && (
                <Typography color="text.secondary" sx={{ display: 'block', mt: 1.5 }} variant="caption">
                  Applies to the x axis, the tooltip heading and the matching table column. Values that are not valid
                  {state.labelKind === 'time' ? ' times' : ' dates'} are left as they are.
                </Typography>
              )}
              {state.showsXAxis && (
                <Typography color="text.secondary" sx={{ display: 'block', mt: 1.5 }} variant="caption">
                  For a chart with many categories, choose “Show every category” and a 45° or 60° label angle.
                </Typography>
              )}
            </Box>

            {(type === 'heatmap' || type === 'boxplot') && columnNames.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography sx={{ mb: 0.5 }} variant="subtitle2">
                    {type === 'heatmap' ? 'Grid fields' : 'Category and value fields'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
                    {type === 'heatmap'
                      ? 'Choose the second axis and the value each cell is shaded by.'
                      : 'Choose the category each box represents and the value its spread is drawn from.'}
                  </Typography>
                  <Stack direction="column" spacing={2}>
                    {type === 'heatmap' && (
                      <FormControl fullWidth>
                        <InputLabel id="le-lens-y-axis-column">Y-axis field</InputLabel>
                        <Select
                          disabled={isSaving}
                          label="Y-axis field"
                          labelId="le-lens-y-axis-column"
                          onChange={(event) => state.setYColumn(event.target.value)}
                          value={state.yColumn}
                        >
                          <MenuItem value="">Automatically choose the second category field</MenuItem>
                          {columnNames.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {type === 'boxplot' && (
                      <FormControl fullWidth>
                        <InputLabel id="le-lens-category-column">Category field</InputLabel>
                        <Select
                          disabled={isSaving}
                          label="Category field"
                          labelId="le-lens-category-column"
                          onChange={(event) => state.setCategoryColumn(event.target.value)}
                          value={state.categoryColumn}
                        >
                          <MenuItem value="">Automatically choose the first non-numeric field</MenuItem>
                          {columnNames.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <FormControl fullWidth>
                      <InputLabel id="le-lens-value-column">Value field</InputLabel>
                      <Select
                        disabled={isSaving}
                        label="Value field"
                        labelId="le-lens-value-column"
                        onChange={(event) => state.setValueColumn(event.target.value)}
                        value={state.valueColumn}
                      >
                        <MenuItem value="">Automatically choose the first numeric field</MenuItem>
                        {columnNames.map((column) => (
                          <MenuItem key={column} value={column}>
                            {column}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </FormatSubRow>
      )}

      {scope === 'chart' && state.showsSeriesGrouping && (
        <FormatSubRow
          icon={<StackedBarChartOutlinedIcon fontSize="small" />}
          label="Series"
          summary={sectionSummary('series')}
        >
          <Stack spacing={2.5}>
            {state.showsSeriesGrouping && (
              <>
                {type === 'line' && (
                  <FormControl fullWidth>
                    <InputLabel id="le-lens-line-style">Line style</InputLabel>
                    <Select
                      disabled={isSaving}
                      label="Line style"
                      labelId="le-lens-line-style"
                      onChange={(event) => state.setLineStyle(event.target.value)}
                      value={state.lineStyle}
                    >
                      <MenuItem value="straight">Straight segments</MenuItem>
                      <MenuItem value="smooth">Smooth curve</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel id="le-lens-series-sort">Series order</InputLabel>
                  <Select
                    disabled={isSaving}
                    label="Series order"
                    labelId="le-lens-series-sort"
                    onChange={(event) => state.setSeriesSort(event.target.value)}
                    value={state.seriesSort}
                  >
                    {seriesSorts.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {state.seriesSort !== 'none' && (
                  <Typography color="text.secondary" sx={{ display: 'block', mt: -1 }} variant="caption">
                    Other always sits at the top: it is a residual rather than a category.
                  </Typography>
                )}
                {state.valueLabelsOn && state.valueLabelMode === 'values' && seriesNames.length > 1 && (
                  <FormControl fullWidth>
                    <InputLabel id="le-lens-value-label-series">On these series</InputLabel>
                    <Select
                      disabled={busy}
                      input={<OutlinedInput label="On these series" />}
                      labelId="le-lens-value-label-series"
                      multiple
                      onChange={(event) =>
                        state.setValueLabelSeries(
                          typeof event.target.value === 'string' ? [event.target.value] : event.target.value,
                        )
                      }
                      renderValue={(chosen) =>
                        chosen.length === 0 || chosen.length === seriesNames.length
                          ? 'Every series'
                          : chosen.map((name) => displayLensFieldName(name, card.config)).join(', ')
                      }
                      value={state.valueLabelSeries}
                    >
                      {seriesNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {displayLensFieldName(name, card.config)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Leave every series selected, or none, to label them all as the card gains series.
                    </FormHelperText>
                  </FormControl>
                )}
                {state.valueLabelsOn && state.valueLabelMode === 'values' && (
                  <Typography color="text.secondary" sx={{ display: 'block', mt: -1 }} variant="caption">
                    Values under 10,000 print in full; larger ones print compactly, as the axis does.
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </FormatSubRow>
      )}

      {scope === 'chart' && state.showsValueAxis && (
        <FormatSubRow
          icon={<FlagOutlinedIcon fontSize="small" />}
          label="Reference lines"
          summary={sectionSummary('guides')}
        >
          <Stack spacing={2}>
            <Typography color="text.secondary" variant="body2">
              Add a target, budget or benchmark directly to the plot. Guides use the same number format as the card and
              remain visible in exports.
            </Typography>
            {state.referenceLines.length > 0 && (
              <Stack spacing={1.5}>
                {state.referenceLines.map((line, index) => (
                  <Stack alignItems="stretch" direction="column" key={`${index}-${line.axis}`} spacing={1.5}>
                    <TextField
                      disabled={busy}
                      error={Boolean(line.value.trim()) && !Number.isFinite(Number(line.value))}
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                      label="Value"
                      onChange={(event) => state.updateReferenceLine(index, { value: event.target.value })}
                      placeholder="e.g. 100000"
                      value={line.value}
                    />
                    <TextField
                      disabled={busy}
                      fullWidth
                      label="Label"
                      onChange={(event) => state.updateReferenceLine(index, { label: event.target.value })}
                      placeholder="e.g. Target"
                      value={line.label}
                    />
                    {type === 'combo' && (
                      <FormControl fullWidth>
                        <InputLabel id={`le-lens-guide-axis-${index}`}>Scale</InputLabel>
                        <Select
                          disabled={busy}
                          label="Scale"
                          labelId={`le-lens-guide-axis-${index}`}
                          onChange={(event) => state.updateReferenceLine(index, { axis: event.target.value })}
                          value={line.axis}
                        >
                          <MenuItem value="primary">Left axis</MenuItem>
                          <MenuItem value="secondary">Right axis</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    <IconButton
                      aria-label={`Remove reference line ${index + 1}`}
                      disabled={busy}
                      onClick={() => state.removeReferenceLine(index)}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
            <Button
              disabled={busy || state.referenceLines.length >= 4}
              onClick={() => state.addReferenceLine()}
              startIcon={<AddIcon />}
              sx={{ alignSelf: 'flex-start' }}
              variant="outlined"
            >
              Add reference line
            </Button>
            {state.referenceLines.length >= 4 && (
              <Typography color="text.secondary" variant="caption">
                Four guides is the most a chart can carry without turning into a grid of annotations.
              </Typography>
            )}
          </Stack>
        </FormatSubRow>
      )}
    </Box>
  );
}
